import { useState, useEffect } from 'react';
import { useCurrentAccountId } from '@zapier/identity';

import type { UseQuery } from '../types';
import type { Result } from './alertBannerNotificationsQuery';
import { alertBannerNotificationsQuery } from './alertBannerNotificationsQuery';

import { BannerProps } from '../banners/types';
import * as RefreshRequired from '../banners/RefreshRequired';
import * as BannerMessage from '../banners/BannerMessage';
import * as UnlimitedAccount from '../banners/UnlimitedAccount';
import * as TeamsTrialing from '../banners/TeamsTrialing';
import * as TeamsTrialEnded from '../banners/TeamsTrialEnded';
import * as TeamsDowngraded from '../banners/TeamsDowngraded';
import * as CompaniesTrialing from '../banners/CompaniesTrialing';
import * as CompaniesTrialEnded from '../banners/CompaniesTrialEnded';
import * as ProfessionalTrialing from '../banners/ProfessionalTrialing';
import * as ProfessionalTrialEnded from '../banners/ProfessionalTrialEnded';
import * as AnnualBillingUpsell from '../banners/AnnualBillingUpsell';
import * as JobsHiring from '../banners/JobsHiring';

type UseAlertOptions = {
  useQuery: UseQuery;
};

type UseAlertReturn = {
  isLoading: boolean;
  data: Result['data'];
  Component?: (props: BannerProps) => JSX.Element | null;
};

const possibleComponents = [
  RefreshRequired,
  BannerMessage,
  UnlimitedAccount,
  CompaniesTrialing,
  CompaniesTrialEnded,
  TeamsTrialing,
  TeamsTrialEnded,
  TeamsDowngraded,
  ProfessionalTrialing,
  ProfessionalTrialEnded,
  AnnualBillingUpsell,
  JobsHiring,
];

export const useAlertBanner = (opts: UseAlertOptions): UseAlertReturn => {
  const [lastRenderedTime, setLastRenderedTime] = useState(Date.now());
  // Rerender every few minutes to ensure data is up to date. This is
  // especially important for `RefreshRequired`.
  useEffect(() => {
    setTimeout(() => {
      setLastRenderedTime(Date.now());
    }, 5 * 60 * 60 * 1000);
  }, [lastRenderedTime]);
  const currentAccountId = useCurrentAccountId();
  const result: Result = opts.useQuery(alertBannerNotificationsQuery, {
    ssr: false,
    variables: {
      accountId: currentAccountId,
    },
  });
  const { data } = result;
  const Component =
    result.loading ||
    result.error ||
    !data ||
    !data.currentUser ||
    !data.currentAccount
      ? undefined
      : possibleComponents.find((component) => {
          return component.shouldRender(data);
        })?.Component;
  return {
    data,
    Component,
    isLoading: result.loading,
  };
};
